import { HttpClient, HttpHeaders, HttpBackend } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { RolesData } from '../../@core/interfaces/common/roles';
import { PatientRegistrationData } from '../../@core/interfaces/patient-services/patient-registration';
import { AbhaService } from './abha-link.service';

@Injectable({
  providedIn: 'root'
})
export class AbhaPrescriptionLinkService {
  private httpDirect: HttpClient;

  constructor(
    private api: HttpClient,
    private rolesService: RolesData,
    private patSrvc: PatientRegistrationData,
    private abhaService: AbhaService,
    httpBackend: HttpBackend,
  ) {
    this.httpDirect = new HttpClient(httpBackend);
  }

  private get isAbhaUser(): boolean {
    return localStorage.getItem('abhaReg') === 'true';
  }

  autoLinkCareContext(tenantId: string, branchId: string, regId: string, visitId: number, hiTypes: string[]): void {
    if (!this.isAbhaUser || !regId || !visitId || !hiTypes?.length) {
      return;
    }

    const patientBlocks = hiTypes.map(hiType => ({
      referenceNumber: regId,
      display: 'regId',
      hiType,
      count: 1,
      careContexts: [{
        referenceNumber: `${hiType}/${visitId}`,
        display: this.buildContextDisplay(hiType, visitId),
      }],
    }));

    const skipReportGeneration = hiTypes.includes('Invoice');

    this.performLink(tenantId, branchId, regId, visitId, patientBlocks, skipReportGeneration);
  }

  private buildContextDisplay(hiType: string, visitId: number): string {
    switch (hiType) {
      case 'OPConsultation':
        return 'OP Visit';
      case 'DischargeSummary':
        return `IP Admission No. ${visitId}`;
      default:
        return hiType;
    }
  }

  private performLink(
    tenantId: string, branchId: string, regId: string, visitId: number,
    patientBlocks: any[], skipReportGeneration: boolean,
  ): void {
    this.patSrvc.getById('patientregistrationmst/' + tenantId + '/' + branchId + '/' + regId)
      .subscribe((patient: any) => {
        const rawAbhaAddress = String(patient?.abhaAddress ?? '').split(',')[0].trim();
        const localPart = rawAbhaAddress.split('@')[0].trim().toLowerCase();
        if (!localPart || localPart === 'null' || localPart === 'undefined') {
          this.triggerSmsNotification(tenantId, branchId, regId, patient?.mobile);
          return;
        }
        const abhaAddress = rawAbhaAddress.replace(/@.*/, '') + '@sbx';
        const abhaNumber = patient?.nhid ? patient.nhid.replaceAll('-', '') : null;
        this.connect(regId, tenantId, branchId, abhaAddress, (requestId, token) => {
          const proceedToValidate = () => {
            this.validateLinkToken(tenantId, branchId, abhaAddress, (accessToken) => {
              this.sendCareContextV3(
                tenantId, branchId, regId, visitId,
                abhaNumber, abhaAddress, token, requestId, accessToken, patientBlocks,
              );
            }, () => {
              this.submitDemographicDetailsV3(tenantId, branchId, regId, requestId, token, abhaNumber, abhaAddress, patient);
            });
          };

          if (skipReportGeneration) {
            proceedToValidate();
            return;
          }
          this.generatePdfsToBlobs(tenantId, branchId, regId, visitId, proceedToValidate);
        }, (linkToken, requestId, token) => {
          this.sendCareContextV3(
            tenantId, branchId, regId, visitId,
            abhaNumber, abhaAddress, token, requestId, linkToken, patientBlocks,
          );
        });
      }, () => { });
  }

  private connect(
    regId: string, tenantId: string, branchId: string, abhaAddress: string,
    onReady: (requestId: string, token: string) => void,
    onLinkToken: (linkToken: string, requestId: string, token: string) => void,
  ): void {
    const socket = new WebSocket(`${environment.abhaWebSocket}carecontext/ws`);
    let requestId: string = null;
    let gatewayToken: string = null;
    let readySent = false;

    socket.onmessage = (event: MessageEvent) => {
      let result: any;
      try {
        result = JSON.parse(event.data);
      } catch {
        return;
      }

      if (result?.generatedId != null && result.generatedId !== '00000000-0000-0000-0000-000000000000') {
        requestId = result.generatedId;
        if (abhaAddress && !readySent) {
          this.tokenCall((token: string) => {
            if (!readySent) {
              readySent = true;
              gatewayToken = token;
              onReady(requestId, token);
            }
          });
        }
      }

      if (result?.error != null) {
        return;
      }

      if (result?.onGenerateTokenReqId != null && result.onGenerateTokenReqId !== '00000000-0000-0000-0000-000000000000') {
        requestId = result.onGenerateTokenReqId;
      }
      
      if (result?.linkToken != null) {
        this.transactionLog(regId, tenantId, branchId, abhaAddress, result.linkToken);
        onLinkToken(result.linkToken, requestId, gatewayToken);
        socket.close();
      }
    };

    socket.onerror = () => {
      socket.close();
    };
  }
  private tokenCall(onToken: (token: string) => void): void {
    this.httpDirect.get(environment.abhaUrl + 'ABHA/GatewaySessions', {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
    }).subscribe((data: any) => {
      onToken(data?.accessToken);
    }, () => { });
  }
  private transactionLog(regId: string, tenantId: string, branchId: string, abhaAddress: string, linkToken: string): void {
    const postData = {
      patientABHA: abhaAddress,
      hipId: localStorage.getItem('hipId') ?? null,
      hiuId: localStorage.getItem('hiuId') ?? null,
      careContextRef: regId,
      linkToken,
      status: 'REQUESTED',
      recordType: 'CARECONTEXT',
      tenantId,
      branchId,
      createdBy: parseInt(localStorage.getItem('userId')),
      createdIp: null,
      regId,
      updatedHIType: '',
    };
    this.rolesService.create(`${environment.apiUrl}/ABHA/createAbhaTransaction`, postData).subscribe(() => { }, () => { });
  }
  private validateLinkToken(
    tenantId: string, branchId: string, abhaAddress: string,
    onValid: (accessToken: string) => void, onInvalid: () => void,
  ): void {
    this.rolesService.create(`${environment.apiUrl}/ABHA/Linktokenvalidation`, {
      abhaAddress,
      tenantId,
      branchId,
    }).subscribe((tokenRes: any) => {
      if (tokenRes?.message === 'Valid') {
        onValid(tokenRes?.token);
      } else {
        onInvalid();
      }
    }, () => { });
  }

  private submitDemographicDetailsV3(
    tenantId: string, branchId: string, regId: string, requestId: string, token: string,
    abhaNumber: string, abhaAddress: string, patient: any,
  ): void {
    const url = environment.abhaUrl + 'carecontext/auth/demographicAuthV3';
    const name = [patient?.firstname, patient?.middlename, patient?.lastname].filter(Boolean).join(' ');
    const gender = patient?.gender === 'Male' ? 'M' : 'F';
    const yearOfBirth = patient?.dob ? new Date(patient.dob).getFullYear() : '';

    const body = {
      tenantId,
      branchId,
      regId,
      abhaNumber: abhaNumber || null,
      abhaAddress,
      name,
      gender,
      yearOfBirth,
    };

    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('Authorization', token)
      .append('Accept', '*/*')
      .append('REQUEST-ID', requestId?.toString())
      .append('TIMESTAMP', new Date().toISOString());

    this.httpDirect.post(url, JSON.stringify(body), { headers }).subscribe({
      next: () => { },
      error: () => { },
    });
  }
  private sendCareContextV3(
    tenantId: string, branchId: string, regId: string, visitId: number,
    abhaNumber: string, abhaAddress: string, token: string, requestId: string,
    accessToken: string, patientBlocks: any[],
  ): void {
    const payload = {
      abhaNumber,
      abhaAddress,
      tenantId,
      branchId,
      token,
      requestId,
      accessToken,
      abhaBaseURL: environment.abhaUrl,
      visitIds: [Number(visitId)],
      regId,
      patients: patientBlocks,
    };

    this.abhaService.linkCareContextV3(payload).subscribe({
      next: () => { },
      error: () => { },
    });
  }

  private triggerSmsNotification(tenantId: string, branchId: string, regId: string, phoneNo: string): void {
    if (!phoneNo) {
      return;
    }

    this.httpDirect.get(environment.abhaUrl + 'ABHA/GatewaySessions', {
      headers: new HttpHeaders().append('Content-Type', 'application/json'),
    }).subscribe((gatewayRes: any) => {
      const url = environment.abhaUrl + 'carecontext/notify';
      const body = {
        phoneNo,
        hipId: localStorage.getItem('hipId'),
        hipName: 'Ezovion hospital',
        regId,
        tenantId,
        branchId,
        abhaId: '',
      };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${gatewayRes?.accessToken}`,
        'X-CM-ID': 'sbx',
      });

      this.httpDirect.post(url, JSON.stringify(body), { headers }).subscribe({
        next: () => { },
        error: () => { },
      });
    }, () => { });
  }

  public generatePdfsToBlobs(tenantId: string, branchId: string, regId: string, visitId: number, onSuccess: () => void): void {
    const payload = {
      tenantId,
      branchId,
      regId,
      visitIds: [Number(visitId)],
    };

    this.api.post(`${environment.apiUrl}/ABHA/GenerateCareContextReports`, payload)
      .subscribe(() => {
        onSuccess();
      }, () => { });
  }
}
